/**
 * Chart Formatters — Utility functions for formatting chart axis labels,
 * tooltip values, and data transformations.
 *
 * Indian locale-aware with ₹ currency support.
 */

// ─── Number Formatters ─────────────────────────────────────────────

/**
 * Format a number using Indian locale (en-IN).
 * Example: 1234567 → "12,34,567"
 */
export function formatNumber(value: number): string {
  return new Intl.NumberFormat('en-IN').format(value);
}

/**
 * Format as Indian Rupee currency.
 * Example: 1234567 → "₹12,34,567"
 */
export function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

/**
 * Format as compact number.
 * Example: 1234567 → "12.3L" (Lakhs)
 */
export function formatCompact(value: number): string {
  if (Math.abs(value) >= 1_00_00_000) {
    return `${(value / 1_00_00_000).toFixed(1)}Cr`;
  }
  if (Math.abs(value) >= 1_00_000) {
    return `${(value / 1_00_000).toFixed(1)}L`;
  }
  if (Math.abs(value) >= 1_000) {
    return `${(value / 1_000).toFixed(1)}K`;
  }
  return String(value);
}

/**
 * Format as compact currency.
 * Example: 1234567 → "₹12.3L"
 */
export function formatCompactCurrency(value: number): string {
  return `₹${formatCompact(value)}`;
}

/**
 * Format as percentage.
 * Example: 0.123 → "12.3%" or 12.3 → "12.3%"
 */
export function formatPercent(value: number, isDecimal = false): string {
  const pct = isDecimal ? value * 100 : value;
  return `${pct.toFixed(1)}%`;
}

// ─── Date Formatters ───────────────────────────────────────────────

/**
 * Format date for chart axis (short month + year).
 * Example: "2025-03-15" → "Mar 25"
 */
export function formatDateAxis(dateStr: string): string {
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-IN', { month: 'short', year: '2-digit' });
  } catch {
    return dateStr;
  }
}

/**
 * Format date for tooltip (full readable format).
 * Example: "2025-03-15" → "15 Mar 2025"
 */
export function formatDateTooltip(dateStr: string): string {
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

/**
 * Format month only.
 * Example: "2025-03-15" → "Mar"
 */
export function formatMonth(dateStr: string): string {
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-IN', { month: 'short' });
  } catch {
    return dateStr;
  }
}

/**
 * Format day + month.
 * Example: "2025-03-15" → "15 Mar"
 */
export function formatDayMonth(dateStr: string): string {
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  } catch {
    return dateStr;
  }
}

// ─── Axis Tick Formatter Factory ────────────────────────────────────

export type TickFormatterType =
  | 'number'
  | 'currency'
  | 'compact'
  | 'compactCurrency'
  | 'percent'
  | 'date'
  | 'month'
  | 'dayMonth';

/**
 * Get a tick formatter function by type.
 * For use with Recharts tickFormatter prop.
 */
export function getTickFormatter(type: TickFormatterType): (value: any) => string {
  switch (type) {
    case 'number':
      return (v) => formatNumber(Number(v));
    case 'currency':
      return (v) => formatCurrency(Number(v));
    case 'compact':
      return (v) => formatCompact(Number(v));
    case 'compactCurrency':
      return (v) => formatCompactCurrency(Number(v));
    case 'percent':
      return (v) => formatPercent(Number(v));
    case 'date':
      return (v) => formatDateAxis(String(v));
    case 'month':
      return (v) => formatMonth(String(v));
    case 'dayMonth':
      return (v) => formatDayMonth(String(v));
    default:
      return (v) => String(v);
  }
}
